import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { FileAssignment } from "@/pages/Private/pages/Courses/enums/FileAssignment.enum";
import { FileTypes } from "@/pages/Private/pages/Courses/enums/FileTypes.enum";
import { IFileInfo } from "@/pages/Private/pages/Courses/components/FilesUploader";
import { useAssignLibraryAssetMutation } from "@/pages/Private/redux/media/media.api";

import { MediaPickerModal } from "./MediaPickerModal";

interface PickFromLibraryButtonProps {
	courseId?: number;
	/** What the chosen file becomes: the scenario's thumbnail, large image or trailer. */
	fileAssignment: FileAssignment;
	contentType: FileTypes;
	disabled?: boolean;
	/** Handed the created course-file, which is the same shape an upload produces. */
	onAssigned: (fileInfo: IFileInfo) => void;
}

/**
 * "Choose from the media library", for the scenario's own three slots.
 *
 * Unlike a phase — which holds a bare URL — these need a `course-file` carrying the right
 * assignment, so choosing here goes through the library's assign path rather than just
 * taking the URL. That is the same call the Medien tab makes, so a trailer chosen here and
 * a trailer chosen there behave identically: the previous one is retired, the wizard
 * session is re-pointed, and the file is shared rather than copied.
 *
 * The result is handed back in the shape an upload produces, so the uploader that hosts
 * this button does not need to know which of the two happened.
 */
export const PickFromLibraryButton: FunctionComponent<PickFromLibraryButtonProps> = ({
	courseId,
	fileAssignment,
	contentType,
	disabled,
	onAssigned,
}) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [assign, { isLoading }] = useAssignLibraryAssetMutation();

	if (!courseId) {
		return null;
	}

	return (
		<>
			<Button
				className="!w-auto"
				color={ButtonColor.ACTION_SECONDARY}
				disabled={disabled || isLoading}
				size={ButtonSize.S}
				title={t("courses.mediaLibrary.choose")}
				type="button"
				onClick={() => setIsOpen(true)}
			/>

			<MediaPickerModal
				contentType={contentType}
				courseId={courseId}
				handleClose={() => setIsOpen(false)}
				isOpened={isOpen}
				onSelect={async ({ kind, id }) => {
					const courseFile = await assign({
						kind,
						assetId: id,
						courseId,
						fileAssignment,
					}).unwrap();

					onAssigned(courseFile as IFileInfo);
				}}
			/>
		</>
	);
};
